import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button, CircularProgress, useTheme, Grid } from '@mui/material';

import FieldRow from 'components/FormElements/FieldRow';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { AppSimpleSelect } from 'components/FormElements';
import { useGetRole } from 'hooks/ui/useGetRole';
import { FirmSelector } from 'components/FirmSelector';
import {
  numberToDisplayString,
  numberToCurrencyString,
} from 'further-ui/utils';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import DistinctLegalCompanyNameSelect from 'components/DistinctLegalCompanyNameSelect';
import { useNotification } from 'hooks/ui/useNotification';
import {
  TaxReliefElegibleType,
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import CashConsiderationFields from './CashConsiderationFields';
import ShareConsiderationFields from './ShareConsiderationFields';
import calculateShareConsiderationValues from './calculate-share-consideration-values';
import api from 'lib/trpcClient';
import ShareClassMultiSelect from 'components/ShareClassMultiSelect';
import { format } from 'date-fns';
import ButtonGroup from 'components/ButtonGroup';
import GridContainer from 'components/GridContainer';
import AllocationSelector from 'components/AllocationSelector';
import Spacing from 'components/Spacing';
import { styled } from '@mui/material';
import { useAllocations } from 'hooks/data/allocation/useAllocations';
import getAllocationLabel from 'utils/allocationLabel';
import { Controller, useForm } from 'react-hook-form';

const Row = styled('div')(() => ({
  display: 'flex',
  maxWidth: '20rem',
  gridGap: '1rem',
  '& > div': {
    width: '100%',
  },
}));

const Li = styled('li')(() => ({
  marginLeft: '1rem',
}));

const StyledRow = styled('div')(() => ({
  display: 'flex',
  minHeight: '42px',
  alignItems: 'center',
  gridGap: '1rem',

  '& > div': {
    width: '100%',
  },
}));

const StyledRowWithDynamicHeight = styled('div')<{ hasError?: boolean }>(
  ({ hasError }) => ({
    display: 'flex',
    height: hasError ? 'auto' : '42px',
    alignItems: hasError ? 'flex-start' : 'center',
    gridGap: '1rem',

    '& > div': {
      width: '100%',
    },
  }),
);

const StyledShareCountUnknown = styled('span')(({ theme }) => ({
  fontStyle: 'italic',
  color: theme.palette.text.greyLabel,
}));

const StyledInputFix = styled('div')(() => ({
  '& .MuiInputBase-input': {
    height: 'auto',
  },
}));

const StyledFieldRow = styled(FieldRow)(() => ({
  minHeight: '42px',
}));

const StyledShareClassesList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
}));

type PreviousExitDetails = {
  firmId?: string;
  firmName?: string;
  exitDate?: Date | string;
  selectedCompany?: { _id: string; legalName?: string; tradingName?: string };
  selectedCompanyId?: string;
  selectedShareClasses?: Array<string>;
  sharesToBeSold?: number;
  sharePrice?: number | string;
  taxableValueSharePrice?: number | string;
  chargeAccruedFees?: boolean;
  taxReliefElegible?: TaxReliefElegibleType;
  taxReliefElegibleAllocationIds?: Array<string>;
  shareAllocationUploadIds?: Array<string>;
  considerationType?: ConsiderationType;
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassIds?: Array<string> | string;
  totalConsiderationShares?: number | string;
  shareConsiderationValuePerShare?: number | string;
  shareConsiderationValueTotal?: number | string;
  shareConsiderationEisEligible?: boolean;
  shareConsiderationAllocationMethod?: ShareConsiderationAllocationMethod;
  permitPartialShareAllocation?: boolean;
};

type ExitDetailsSubmitData = {
  firmId?: string;
  firmName?: string;
  exitDate: Date;
  selectedCompany?: { _id: string; legalName?: string; tradingName?: string };
  selectedCompanyId?: string;
  selectedShareClasses?: Array<string>;
  sharesToBeSold?: number;
  sharePrice: number;
  taxableValueSharePrice: number;
  chargeAccruedFees?: boolean;
  taxReliefElegible?: TaxReliefElegibleType;
  taxReliefElegibleAllocationIds?: Array<string>;
  shareAllocationUploadIds?: Array<string>;
  selectedAllocations?: Array<string>;
  shareholdingsToBeExited: Array<{
    shareholdingId: string;
    sharesAllocatedForSale: number;
    totalSaleValue: number;
    taxableValue: number;
    performanceFee: number;
  }>;
  considerationType?: ConsiderationType;
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassId?: string;
  totalConsiderationShares?: number;
  shareConsiderationValuePerShare?: number;
  shareConsiderationValueTotal?: number;
  shareConsiderationEisEligible?: boolean;
  shareConsiderationAllocationMethod?: ShareConsiderationAllocationMethod;
  permitPartialShareAllocation?: boolean;
};

type ExitDetailsFormProps = {
  onSubmit: (details: ExitDetailsSubmitData) => void;
  exitDetailsStepCompleted: boolean;
  previousExitDetails?: PreviousExitDetails;
  isBeingEdited?: boolean;
};

export type ExitDetailsFormValues = {
  firmId: string;
  firmName: string;
  exitDate: Date;
  selectedCompany: { _id: string; legalName?: string; tradingName?: string };
  sharesToBeSold: number;
  sharePrice: string;
  taxableValueSharePrice: string;
  isReceivedValueSharePriceEdited: boolean;
  chargeAccruedFees: boolean | undefined;
  taxReliefElegible: TaxReliefElegibleType | undefined;
  selectedShareClasses: Array<string>;
  selectedAllocations: Array<string>;
  taxReliefElegibleAllocationIds: Array<string>;
  considerationType: ConsiderationType | undefined;
  shareConsiderationCompanyId: string | undefined;
  shareConsiderationShareClassId: string | undefined;
  totalConsiderationShares: string;
  shareConsiderationValuePerShare: string;
  shareConsiderationValueTotal: string;
  shareConsiderationEisEligible: boolean | undefined;
  shareConsiderationAllocationMethod:
    | ShareConsiderationAllocationMethod
    | undefined;
  permitPartialShareAllocation: boolean;
};

const ExitDetailsForm: React.FC<ExitDetailsFormProps> = ({
  onSubmit,
  exitDetailsStepCompleted,
  previousExitDetails,
  isBeingEdited = false,
}) => {
  const theme = useTheme();
  const { isSuperAdmin, firmId: usersFirmId } = useGetRole();
  const isEditMode = isBeingEdited;
  const notification = useNotification();
  const apiUtils = api.useUtils();
  const lastManuallyEditedField = useRef<
    'perShare' | 'total' | 'shares' | null
  >(null);
  const isCalculating = useRef<boolean>(false);

  // Helper to get company ID from previousExitDetails
  const getCompanyIdFromPrevious = (): string | undefined => {
    if (!isEditMode || !previousExitDetails?.selectedCompany) return undefined;
    if (
      typeof previousExitDetails.selectedCompany === 'object' &&
      '_id' in previousExitDetails.selectedCompany
    ) {
      return previousExitDetails.selectedCompany._id;
    }
    return previousExitDetails.selectedCompany;
  };

  // Helper to get share class ID from previousExitDetails
  const getShareClassIdFromPrevious = (): string | undefined => {
    if (!isEditMode || !previousExitDetails?.shareConsiderationShareClassIds)
      return undefined;
    const shareClassIds = previousExitDetails.shareConsiderationShareClassIds;
    if (Array.isArray(shareClassIds) && shareClassIds.length > 0) {
      return shareClassIds[0];
    }
    if (typeof shareClassIds === 'string') {
      return shareClassIds;
    }
    return undefined;
  };

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExitDetailsFormValues>({
    defaultValues: {
      firmId: isEditMode
        ? previousExitDetails?.firmId || ''
        : usersFirmId || '',
      exitDate:
        isEditMode && previousExitDetails?.exitDate
          ? new Date(previousExitDetails.exitDate)
          : new Date(),
      selectedCompany: {
        _id: getCompanyIdFromPrevious() ?? '',
        legalName: previousExitDetails?.selectedCompany?.legalName ?? '',
        tradingName: previousExitDetails?.selectedCompany?.tradingName ?? '',
      },
      sharesToBeSold: isEditMode ? previousExitDetails?.sharesToBeSold || 0 : 0,
      sharePrice: isEditMode
        ? previousExitDetails?.sharePrice?.toString() || '0'
        : '0',
      taxableValueSharePrice: isEditMode
        ? previousExitDetails?.taxableValueSharePrice?.toString() || '0'
        : '0',
      isReceivedValueSharePriceEdited: false,
      chargeAccruedFees: isEditMode
        ? previousExitDetails?.chargeAccruedFees
        : undefined,
      taxReliefElegible: isEditMode
        ? previousExitDetails?.taxReliefElegible
        : undefined,
      selectedShareClasses: isEditMode
        ? previousExitDetails?.selectedShareClasses || []
        : [],
      selectedAllocations: isEditMode
        ? previousExitDetails?.shareAllocationUploadIds || []
        : [],
      taxReliefElegibleAllocationIds: isEditMode
        ? previousExitDetails?.taxReliefElegibleAllocationIds || []
        : [],
      considerationType: undefined,
      shareConsiderationCompanyId: isEditMode
        ? previousExitDetails?.shareConsiderationCompanyId
        : undefined,
      shareConsiderationShareClassId: getShareClassIdFromPrevious(),
      totalConsiderationShares: isEditMode
        ? previousExitDetails?.totalConsiderationShares?.toString() || '0'
        : '0',
      shareConsiderationValuePerShare: isEditMode
        ? previousExitDetails?.shareConsiderationValuePerShare?.toString() ||
          '0'
        : '0',
      shareConsiderationValueTotal: isEditMode
        ? previousExitDetails?.shareConsiderationValueTotal?.toString() || '0'
        : '0',
      shareConsiderationEisEligible: isEditMode
        ? previousExitDetails?.shareConsiderationEisEligible
        : false,
      shareConsiderationAllocationMethod:
        isEditMode && previousExitDetails?.shareConsiderationAllocationMethod
          ? previousExitDetails.shareConsiderationAllocationMethod ===
              ShareConsiderationAllocationMethod.Upload ||
            previousExitDetails.shareConsiderationAllocationMethod ===
              ShareConsiderationAllocationMethod.Automatic
            ? previousExitDetails.shareConsiderationAllocationMethod
            : undefined
          : undefined,
      permitPartialShareAllocation: isEditMode
        ? previousExitDetails?.permitPartialShareAllocation ?? false
        : false,
    },
  });

  // Watch form values used in JSX conditionals
  const firmId = watch('firmId');
  const exitDate = watch('exitDate');
  const selectedCompany = watch('selectedCompany');
  const isReceivedValueSharePriceEdited = watch(
    'isReceivedValueSharePriceEdited',
  );
  const taxReliefElegible = watch('taxReliefElegible');
  const selectedShareClasses = watch('selectedShareClasses');
  const selectedAllocations = watch('selectedAllocations');
  const taxReliefElegibleAllocationIds = watch(
    'taxReliefElegibleAllocationIds',
  );
  const considerationType = watch('considerationType');
  const shareConsiderationCompanyId = watch('shareConsiderationCompanyId');
  const totalConsiderationShares = watch('totalConsiderationShares');
  const shareConsiderationValuePerShare = watch(
    'shareConsiderationValuePerShare',
  );
  const shareConsiderationValueTotal = watch('shareConsiderationValueTotal');
  const shareConsiderationAllocationMethod = watch(
    'shareConsiderationAllocationMethod',
  );

  // Local state for computed/derived values
  const [maxSharesAvailableForSale, setMaxSharesAvailableForSale] =
    useState<number>();
  const [
    maxSharesAvailableForSaleLoading,
    setMaxSharesAvailableForSaleLoading,
  ] = useState(false);

  // For edit mode: fetch allocations to display
  const { data: allocationsData, isLoading: isLoadingAllocations } =
    useAllocations({
      params: {
        firmId: firmId ?? '',
        companyIds:
          selectedCompany?._id && typeof selectedCompany._id === 'string'
            ? [selectedCompany._id]
            : [],
      },
      enabled:
        isEditMode &&
        !!selectedCompany &&
        !!firmId &&
        !!previousExitDetails?.selectedCompany,
    });

  const allocations = allocationsData?.uploadShareHoldings.map(
    (allocation) => ({
      _id: allocation._id,
      allocationName: getAllocationLabel(allocation),
    }),
  );

  // For edit mode: fetch share classes to display
  const { data: shareClassesData } = api.investee.listShareClasses.useQuery(
    {
      investeeId: selectedCompany?._id ?? '',
    },
    {
      enabled:
        isEditMode &&
        !!selectedCompany &&
        !!selectedCompany?._id &&
        !!previousExitDetails?.selectedShareClasses?.length,
    },
  );

  const shareClasses = shareClassesData?.result
    .filter((shareClass) =>
      previousExitDetails?.selectedShareClasses?.includes(
        shareClass.investeeId,
      ),
    )
    .map((shareClass) => ({
      _id: shareClass.investeeId,
      shareClassName: shareClass.shareClass || 'Ordinary',
    }));

  // Query for exit companies - only those with current shareholdings
  const { data: uniqueCompaniesByLegalName } =
    api.investee.listUniqueInvestees.useQuery(
      {
        firmId: firmId ?? '',
        currentShareholdingsOnly: true,
      },
      {
        enabled: !!firmId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  // Query for share consideration companies - all companies in the firm
  const { data: allCompaniesForShareConsideration } =
    api.investee.listUniqueInvestees.useQuery(
      {
        firmId: firmId ?? '',
        currentShareholdingsOnly: false,
      },
      {
        enabled: !!firmId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  // Get the legal name of the company being exited
  const exitingCompany = uniqueCompaniesByLegalName?.result?.find(
    (investee) => investee._id === selectedCompany?._id,
  );
  const exitingCompanyLegalName = exitingCompany?.legalName;

  // Filter out companies with the same legal name as the exiting company
  // Use allCompaniesForShareConsideration for share consideration dropdown
  const filteredConsiderationCompanies =
    allCompaniesForShareConsideration?.result?.filter(
      (investee) => investee.legalName !== exitingCompanyLegalName,
    ) || [];

  const disableEdit = exitDetailsStepCompleted;

  // Set values from previousExitDetails in edit mode
  useEffect(() => {
    if (isEditMode && previousExitDetails) {
      const companyId =
        previousExitDetails.selectedCompany &&
        typeof previousExitDetails.selectedCompany === 'object' &&
        '_id' in previousExitDetails.selectedCompany
          ? previousExitDetails.selectedCompany._id
          : previousExitDetails.selectedCompany;

      const shareClassId = previousExitDetails.shareConsiderationShareClassIds
        ? Array.isArray(previousExitDetails.shareConsiderationShareClassIds) &&
          previousExitDetails.shareConsiderationShareClassIds.length > 0
          ? previousExitDetails.shareConsiderationShareClassIds[0]
          : typeof previousExitDetails.shareConsiderationShareClassIds ===
            'string'
          ? previousExitDetails.shareConsiderationShareClassIds
          : undefined
        : undefined;

      reset({
        firmId: previousExitDetails.firmId || '',
        exitDate: previousExitDetails.exitDate
          ? previousExitDetails.exitDate instanceof Date
            ? previousExitDetails.exitDate
            : new Date(previousExitDetails.exitDate)
          : new Date(),
        selectedCompany: {
          _id: companyId,
          legalName: previousExitDetails.selectedCompany?.legalName,
          tradingName: previousExitDetails.selectedCompany?.tradingName,
        },
        sharesToBeSold: previousExitDetails.sharesToBeSold || 0,
        sharePrice: previousExitDetails.sharePrice?.toString() || '0',
        taxableValueSharePrice:
          previousExitDetails.taxableValueSharePrice?.toString() || '0',
        isReceivedValueSharePriceEdited: false,
        chargeAccruedFees: previousExitDetails.chargeAccruedFees,
        taxReliefElegible: previousExitDetails.taxReliefElegible,
        selectedShareClasses: previousExitDetails.selectedShareClasses || [],
        selectedAllocations: previousExitDetails.shareAllocationUploadIds || [],
        taxReliefElegibleAllocationIds:
          previousExitDetails.taxReliefElegibleAllocationIds || [],
        considerationType: undefined,
        shareConsiderationCompanyId:
          previousExitDetails.shareConsiderationCompanyId,
        shareConsiderationShareClassId: shareClassId,
        totalConsiderationShares:
          previousExitDetails.totalConsiderationShares?.toString() || '0',
        shareConsiderationValuePerShare:
          previousExitDetails.shareConsiderationValuePerShare?.toString() ||
          '0',
        shareConsiderationValueTotal:
          previousExitDetails.shareConsiderationValueTotal?.toString() || '0',
        shareConsiderationEisEligible:
          previousExitDetails.shareConsiderationEisEligible,
        shareConsiderationAllocationMethod:
          previousExitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Upload ||
          previousExitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Automatic
            ? previousExitDetails.shareConsiderationAllocationMethod
            : undefined,
        permitPartialShareAllocation:
          previousExitDetails.permitPartialShareAllocation ?? false,
      });
    }
  }, [isEditMode, previousExitDetails, reset]);

  // Edit mode: allocation display helpers
  const allAllocationsSelected =
    isEditMode &&
    previousExitDetails?.shareAllocationUploadIds?.length ===
      allocations?.length;

  const noAllocationsSelected =
    isEditMode && !previousExitDetails?.shareAllocationUploadIds?.length;

  const someAllocationsSelected =
    isEditMode &&
    (previousExitDetails?.shareAllocationUploadIds?.length ?? 0) > 0 &&
    (previousExitDetails?.shareAllocationUploadIds?.length ?? 0) <
      (allocations?.length ?? 0);

  const resetForm = () => {
    if (isEditMode) return; // Don't reset in edit mode
    reset({
      firmId: usersFirmId || '',
      exitDate: new Date(),
      selectedCompany: undefined,
      selectedShareClasses: [],
      selectedAllocations: [],
      sharesToBeSold: 0,
      sharePrice: '0',
      chargeAccruedFees: undefined,
      taxReliefElegible: undefined,
      taxReliefElegibleAllocationIds: [],
      taxableValueSharePrice: '0',
      isReceivedValueSharePriceEdited: false,
      considerationType: undefined,
      shareConsiderationCompanyId: undefined,
      shareConsiderationShareClassId: undefined,
      totalConsiderationShares: '0',
      shareConsiderationValuePerShare: '0',
      shareConsiderationValueTotal: '0',
      shareConsiderationEisEligible: false,
      shareConsiderationAllocationMethod: undefined,
      permitPartialShareAllocation: false,
    });
    setMaxSharesAvailableForSale(undefined);
  };

  // Restore form values when going back (exitDetailsStepCompleted becomes false)
  useEffect(() => {
    if (!exitDetailsStepCompleted && !isEditMode) {
      if (previousExitDetails) {
        // Restore from previousExitDetails instead of resetting
        const companyId =
          previousExitDetails.selectedCompany &&
          typeof previousExitDetails.selectedCompany === 'object' &&
          '_id' in previousExitDetails.selectedCompany
            ? previousExitDetails.selectedCompany._id
            : previousExitDetails.selectedCompany;

        const shareClassId = previousExitDetails.shareConsiderationShareClassIds
          ? Array.isArray(
              previousExitDetails.shareConsiderationShareClassIds,
            ) && previousExitDetails.shareConsiderationShareClassIds.length > 0
            ? previousExitDetails.shareConsiderationShareClassIds[0]
            : typeof previousExitDetails.shareConsiderationShareClassIds ===
              'string'
            ? previousExitDetails.shareConsiderationShareClassIds
            : undefined
          : undefined;

        reset({
          firmId: previousExitDetails.firmId || usersFirmId || '',
          exitDate: previousExitDetails.exitDate
            ? previousExitDetails.exitDate instanceof Date
              ? previousExitDetails.exitDate
              : new Date(previousExitDetails.exitDate)
            : new Date(),
          selectedCompany: {
            _id: companyId ?? '',
            legalName: previousExitDetails.selectedCompany?.legalName,
            tradingName: previousExitDetails.selectedCompany?.tradingName,
          },
          sharesToBeSold: previousExitDetails.sharesToBeSold || 0,
          sharePrice: previousExitDetails.sharePrice?.toString() || '0',
          taxableValueSharePrice:
            previousExitDetails.taxableValueSharePrice?.toString() || '0',
          isReceivedValueSharePriceEdited: false,
          chargeAccruedFees: previousExitDetails.chargeAccruedFees,
          taxReliefElegible: previousExitDetails.taxReliefElegible,
          selectedShareClasses: previousExitDetails.selectedShareClasses || [],
          selectedAllocations:
            previousExitDetails.shareAllocationUploadIds || [],
          taxReliefElegibleAllocationIds:
            previousExitDetails.taxReliefElegibleAllocationIds || [],
          considerationType: previousExitDetails.considerationType,
          shareConsiderationCompanyId:
            previousExitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId: shareClassId,
          totalConsiderationShares:
            previousExitDetails.totalConsiderationShares?.toString() || '0',
          shareConsiderationValuePerShare:
            previousExitDetails.shareConsiderationValuePerShare?.toString() ||
            '0',
          shareConsiderationValueTotal:
            previousExitDetails.shareConsiderationValueTotal?.toString() || '0',
          shareConsiderationEisEligible:
            previousExitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            previousExitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            previousExitDetails.permitPartialShareAllocation ?? false,
        });
      } else {
        // Only reset if there's no previousExitDetails to restore from
        resetForm();
      }
    }
  }, [
    exitDetailsStepCompleted,
    isEditMode,
    previousExitDetails,
    reset,
    usersFirmId,
  ]);

  useEffect(() => {
    const fetchMaxSharesAvailableForSale = async () => {
      if (
        selectedShareClasses.length > 0 &&
        exitDate &&
        !isEditMode &&
        firmId
      ) {
        setMaxSharesAvailableForSale(undefined);
        setMaxSharesAvailableForSaleLoading(true);

        try {
          const sumSharesHeldOnDateData =
            await apiUtils.exit.sumSharesHeldOnDate.fetch({
              firmId,
              companyIds: selectedShareClasses,
              exitDate: format(exitDate, 'dd/MM/yyyy'),
              shareAllocationUploadIds: selectedAllocations,
            });

          setMaxSharesAvailableForSale(sumSharesHeldOnDateData ?? 0);
        } catch (error: any) {
          notification.error(
            error?.data?.cause ??
              error?.message ??
              'Error fetching shares held on date.',
          );
        } finally {
          setMaxSharesAvailableForSaleLoading(false);
        }
      }
    };
    void fetchMaxSharesAvailableForSale();
  }, [selectedAllocations, selectedShareClasses, exitDate, isEditMode, firmId]);

  // Wrapper function to call the extracted helper with current state
  const handleCalculateShareConsiderationValues = (
    changedField: 'perShare' | 'total' | 'shares',
    newValue?: string,
  ) => {
    calculateShareConsiderationValues({
      changedField,
      newValue,
      considerationType,
      totalConsiderationShares,
      shareConsiderationValuePerShare,
      shareConsiderationValueTotal,
      isCalculating,
      setValue,
    });
  };

  const validateForm = (data: ExitDetailsFormValues) => {
    clearErrors();
    let isValid = true;

    if (!data.exitDate) {
      setError('exitDate', {
        message: 'Please select the date the exit happened.',
      });
      isValid = false;
    }
    if (!isEditMode) {
      if (!data.selectedCompany) {
        setError('selectedCompany', { message: 'Please select a company.' });
        isValid = false;
      }
      if (
        !data.selectedShareClasses ||
        data.selectedShareClasses.length === 0
      ) {
        setError('selectedShareClasses', {
          message: 'Please select at least one share class.',
        });
        isValid = false;
      }
      if (!data.sharesToBeSold || Number(data.sharesToBeSold) <= 0) {
        setError('sharesToBeSold', {
          message: 'Please enter the number of shares to be sold.',
        });
        isValid = false;
      }
      if (data.sharesToBeSold > (maxSharesAvailableForSale ?? 0)) {
        setError('sharesToBeSold', {
          message:
            'Please ensure the number of shares to be sold are equal to, or below, the total number of shares available.',
        });
        isValid = false;
      }
    }
    if (!data.considerationType) {
      setError('considerationType', {
        message: 'Please select a consideration type.',
      });
      isValid = false;
    }
    if (data.considerationType === ConsiderationType.Cash) {
      if (isEditMode) {
        const previousSharePrice = previousExitDetails?.sharePrice
          ? Number(previousExitDetails.sharePrice)
          : 0;
        if (!data.sharePrice || Number(data.sharePrice) < previousSharePrice) {
          setError('sharePrice', {
            message: `The received value share price must be equal to or greater than the original received value share price of ${numberToCurrencyString(
              previousSharePrice,
            )}. To edit an exit to a lower overall share price, please delete the original exit and process it again.`,
          });
          isValid = false;
        }
      } else {
        if (
          data.sharePrice === null ||
          data.sharePrice === undefined ||
          data.sharePrice === '' ||
          Number(data.sharePrice) < 0
        ) {
          setError('sharePrice', { message: 'Please enter the share price.' });
          isValid = false;
        }
      }
      if (data.chargeAccruedFees !== false && data.chargeAccruedFees !== true) {
        setError('chargeAccruedFees', { message: 'Please select an option.' });
        isValid = false;
      }
      if (isEditMode) {
        if (
          !data.taxableValueSharePrice &&
          Number(data.taxableValueSharePrice) !== 0
        ) {
          setError('taxableValueSharePrice', {
            message: 'Please enter a value for the taxable value share price.',
          });
          isValid = false;
        }
      }
      if (Number(data.sharePrice) > Number(data.taxableValueSharePrice)) {
        setError('taxableValueSharePrice', {
          message:
            'The taxable value share price must be equal to or greater than the received value share price.',
        });
        isValid = false;
      }
      if (!data.taxReliefElegible) {
        setError('taxReliefElegible', { message: 'Please select an option.' });
        isValid = false;
      }

      if (
        data.taxReliefElegible === TaxReliefElegibleType.Partial &&
        !data.taxReliefElegibleAllocationIds?.length
      ) {
        setError('taxReliefElegibleAllocationIds', {
          message: 'Please select at least one allocation.',
        });
        isValid = false;
      }
    }
    if (data.considerationType === ConsiderationType.Share) {
      if (!data.shareConsiderationCompanyId) {
        setError('shareConsiderationCompanyId', {
          message: 'Please select a share consideration company.',
        });
        isValid = false;
      }
      if (!data.shareConsiderationShareClassId) {
        setError('shareConsiderationShareClassId', {
          message: 'Please select a consideration share class.',
        });
        isValid = false;
      }
      if (
        !data.totalConsiderationShares ||
        Number(data.totalConsiderationShares) <= 0
      ) {
        setError('totalConsiderationShares', {
          message: 'Please enter the total consideration shares.',
        });
        isValid = false;
      }
      if (
        data.shareConsiderationValuePerShare === undefined ||
        data.shareConsiderationValuePerShare === null ||
        Number(data.shareConsiderationValuePerShare) < 0
      ) {
        setError('shareConsiderationValuePerShare', {
          message: 'Please enter the share consideration value per share.',
        });
        isValid = false;
      }
      if (
        data.shareConsiderationValueTotal === undefined ||
        data.shareConsiderationValueTotal === null ||
        Number(data.shareConsiderationValueTotal) < 0
      ) {
        setError('shareConsiderationValueTotal', {
          message: 'Please enter the total share consideration value.',
        });
        isValid = false;
      }
      if (data.shareConsiderationEisEligible === undefined) {
        setError('shareConsiderationEisEligible', {
          message:
            'Please select whether the share consideration is S/EIS eligible.',
        });
        isValid = false;
      }
      if (!data.shareConsiderationAllocationMethod) {
        setError('shareConsiderationAllocationMethod', {
          message: 'Please select an allocation method.',
        });
        isValid = false;
      }
      if (data.permitPartialShareAllocation === undefined) {
        setError('permitPartialShareAllocation', {
          message: 'Please select whether to permit partial share allocation.',
        });
        isValid = false;
      }
      if (!data.taxReliefElegible) {
        setError('taxReliefElegible', { message: 'Please select an option.' });
        isValid = false;
      }
      if (
        data.taxReliefElegible === TaxReliefElegibleType.Partial &&
        !data.taxReliefElegibleAllocationIds?.length
      ) {
        setError('taxReliefElegibleAllocationIds', {
          message: 'Please select at least one allocation.',
        });
        isValid = false;
      }
    }

    return isValid;
  };

  const onSubmitForm = (data: ExitDetailsFormValues) => {
    if (!validateForm(data)) {
      return;
    }
    if (isEditMode) {
      const selectedCompany =
        previousExitDetails?.selectedCompany &&
        typeof previousExitDetails.selectedCompany === 'object' &&
        '_id' in previousExitDetails.selectedCompany
          ? previousExitDetails.selectedCompany
          : undefined;

      const submitData = {
        ...previousExitDetails,
        selectedCompany,
        exitDate: data.exitDate,
        considerationType: data.considerationType,
        sharePrice: Number(data.sharePrice),
        taxableValueSharePrice: Number(data.taxableValueSharePrice),
        chargeAccruedFees: data.chargeAccruedFees,
        taxReliefElegible: data.taxReliefElegible,
        taxReliefElegibleAllocationIds: data.taxReliefElegibleAllocationIds,
        shareConsiderationCompanyId: data.shareConsiderationCompanyId,
        shareConsiderationShareClassId: data.shareConsiderationShareClassId,
        totalConsiderationShares: data.totalConsiderationShares
          ? Number(data.totalConsiderationShares)
          : undefined,
        shareConsiderationValuePerShare: data.shareConsiderationValuePerShare
          ? Number(data.shareConsiderationValuePerShare)
          : undefined,
        shareConsiderationValueTotal: data.shareConsiderationValueTotal
          ? Number(data.shareConsiderationValueTotal)
          : undefined,
        shareConsiderationEisEligible: data.shareConsiderationEisEligible,
        shareConsiderationAllocationMethod:
          data.shareConsiderationAllocationMethod,
        permitPartialShareAllocation: data.permitPartialShareAllocation,
        shareholdingsToBeExited: [],
      };

      onSubmit(submitData);
    } else {
      const submitData = {
        firmId: data.firmId,
        exitDate: data.exitDate,
        considerationType: data.considerationType,
        selectedCompanyId: data.selectedCompany._id ?? '',
        selectedCompany: {
          _id: data.selectedCompany._id ?? '',
          legalName: data.selectedCompany.legalName ?? '',
          tradingName: data.selectedCompany.tradingName ?? '',
        },
        selectedShareClasses: data.selectedShareClasses,
        sharesToBeSold: Number(data.sharesToBeSold),
        sharePrice: Number(data.sharePrice),
        taxableValueSharePrice: Number(data.taxableValueSharePrice),
        chargeAccruedFees: data.chargeAccruedFees,
        taxReliefElegible: data.taxReliefElegible,
        taxReliefElegibleAllocationIds: data.taxReliefElegibleAllocationIds,
        selectedAllocations: data.selectedAllocations,
        shareConsiderationCompanyId: data.shareConsiderationCompanyId,
        shareConsiderationShareClassId: data.shareConsiderationShareClassId,
        totalConsiderationShares: data.totalConsiderationShares
          ? Number(data.totalConsiderationShares)
          : undefined,
        shareConsiderationValuePerShare: data.shareConsiderationValuePerShare
          ? Number(data.shareConsiderationValuePerShare)
          : undefined,
        shareConsiderationValueTotal: data.shareConsiderationValueTotal
          ? Number(data.shareConsiderationValueTotal)
          : undefined,
        shareConsiderationEisEligible: data.shareConsiderationEisEligible,
        shareConsiderationAllocationMethod:
          data.shareConsiderationAllocationMethod,
        permitPartialShareAllocation: data.permitPartialShareAllocation,
        shareholdingsToBeExited: [],
        companyIds: data.selectedShareClasses,
      };

      onSubmit(submitData);
    }
  };

  return (
    <GridContainer>
      <Grid size={{ xs: 12, md: 12, lg: 8 }}>
        <Spacing>
          {isSuperAdmin && (
            <StyledFieldRow title="Firm" centerTitle>
              <StyledRow>
                {isEditMode ? (
                  <Row>{previousExitDetails?.firmName}</Row>
                ) : (
                  <FirmSelector
                    firmId={firmId}
                    onChange={(value) => {
                      if (firmId) {
                        setValue('selectedCompany', {
                          _id: '',
                          legalName: '',
                          tradingName: '',
                        });
                        setValue('selectedShareClasses', []);
                        setValue('selectedAllocations', []);
                      }
                      setValue('firmId', value || '');
                    }}
                    disabled={disableEdit}
                  />
                )}
              </StyledRow>
            </StyledFieldRow>
          )}
          <StyledFieldRow
            title={isEditMode ? 'Exit update date' : 'Exit date'}
            centerTitle
          >
            <StyledRow>
              <Controller
                name="exitDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppDatePicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    disableFuture={true}
                    helperText={error?.message}
                    error={!!error}
                    disabled={disableEdit}
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>
          <StyledFieldRow title="Company" centerTitle>
            <StyledRow>
              {isEditMode ? (
                <Row>{previousExitDetails?.selectedCompany?.tradingName}</Row>
              ) : firmId ? (
                <Controller
                  name="selectedCompany"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const handleCompanyChange = (
                      companyId: string | undefined,
                    ) => {
                      if (companyId) {
                        const selectedCompanyData =
                          uniqueCompaniesByLegalName?.result?.find(
                            (investee) => investee._id === companyId,
                          );
                        if (selectedCompanyData) {
                          const companyObject = {
                            _id: selectedCompanyData._id,
                            legalName: selectedCompanyData.legalName,
                            tradingName: selectedCompanyData.tradingName,
                          };
                          // Always clear share classes and allocations when company changes
                          // Check if this is a different company than the current one
                          if (
                            !field.value?._id ||
                            field.value._id !== companyObject._id
                          ) {
                            setValue('selectedShareClasses', []);
                            setValue('selectedAllocations', []);
                          }
                          field.onChange(companyObject);
                        }
                      } else {
                        // Clear the field but maintain the structure
                        setValue('selectedShareClasses', []);
                        setValue('selectedAllocations', []);
                        field.onChange({
                          _id: '',
                          legalName: '',
                          tradingName: '',
                        });
                      }
                    };

                    return (
                      <DistinctLegalCompanyNameSelect
                        firmId={firmId}
                        value={field.value?._id ?? ''}
                        handleChangeCallback={handleCompanyChange}
                        error={!!error}
                        helperText={error?.message}
                        disabled={disableEdit}
                        currentShareholdingsOnly
                      />
                    );
                  }}
                />
              ) : (
                <StyledShareCountUnknown>
                  First select a firm
                </StyledShareCountUnknown>
              )}
            </StyledRow>
          </StyledFieldRow>
          {!isEditMode && (
            <>
              <StyledFieldRow title="Share class(es)" centerTitle>
                <StyledRow>
                  {firmId && selectedCompany ? (
                    <Controller
                      name="selectedShareClasses"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <ShareClassMultiSelect
                          firmId={firmId}
                          selectedCompanyId={selectedCompany._id ?? ''}
                          value={field.value}
                          onChange={(companyIds) => {
                            field.onChange(companyIds);
                            if (companyIds.length !== field.value.length) {
                              setValue('selectedAllocations', []);
                            }
                          }}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit}
                          currentShareholdingsOnly
                        />
                      )}
                    />
                  ) : (
                    <StyledShareCountUnknown>
                      First select a firm and a company
                    </StyledShareCountUnknown>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Allocations" centerTitle>
                <StyledRow>
                  {firmId && selectedShareClasses.length > 0 ? (
                    <Controller
                      name="selectedAllocations"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <AllocationSelector
                          firmId={firmId}
                          companyIds={selectedShareClasses}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit || !selectedShareClasses.length}
                          currentShareholdingsOnly
                          endDate={exitDate}
                        />
                      )}
                    />
                  ) : (
                    <StyledShareCountUnknown>
                      First select a firm and share class(es)
                    </StyledShareCountUnknown>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Total shares" centerTitle>
                <StyledRow>
                  {!selectedCompany ? (
                    <StyledShareCountUnknown>
                      First select a company
                    </StyledShareCountUnknown>
                  ) : maxSharesAvailableForSaleLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    numberToDisplayString(maxSharesAvailableForSale ?? 0, {
                      maximumFractionDigits: 10,
                    })
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow
                title="Shares to be sold"
                centerTitle
                tooltipText={
                  <span>
                    If there has been a share split between the company's last
                    share price update and this exit, please update this in the
                    share split function for this company{' '}
                    {selectedCompany ? (
                      <NavLink
                        style={{ color: theme.palette.primary[400] }}
                        target="_blank"
                        to={createShareSplitAddRoute(selectedCompany._id ?? '')}
                      >
                        here
                      </NavLink>
                    ) : (
                      ''
                    )}{' '}
                    before processing an exit.
                  </span>
                }
              >
                <StyledRowWithDynamicHeight hasError={!!errors.sharesToBeSold}>
                  <Controller
                    name="sharesToBeSold"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <StyledInputFix>
                        <AppNumberInput
                          value={field.value}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit}
                          min={0}
                          max={maxSharesAvailableForSale}
                          decimals={10}
                          onChange={(value) => field.onChange(value)}
                        />
                      </StyledInputFix>
                    )}
                  />

                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={disableEdit}
                    onClick={() =>
                      setValue('sharesToBeSold', maxSharesAvailableForSale ?? 0)
                    }
                  >
                    All
                  </Button>
                </StyledRowWithDynamicHeight>
              </StyledFieldRow>
            </>
          )}
          {isEditMode && (
            <>
              <StyledFieldRow title="Share class(es)">
                <StyledRow>
                  {shareClasses && shareClasses.length > 0 ? (
                    <StyledShareClassesList>
                      {shareClasses.map((shareClass) => (
                        <div key={shareClass._id}>
                          {shareClass.shareClassName}
                        </div>
                      ))}
                    </StyledShareClassesList>
                  ) : (
                    <span>Loading...</span>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Allocations" centerTitle>
                <StyledRow>
                  {someAllocationsSelected && !isLoadingAllocations && (
                    <ul>
                      {allocations
                        ?.filter((allocation) =>
                          previousExitDetails?.shareAllocationUploadIds?.includes(
                            allocation._id,
                          ),
                        )
                        .map((allocation) => (
                          <Li key={allocation._id}>
                            {allocation.allocationName}
                          </Li>
                        ))}
                    </ul>
                  )}
                  {isLoadingAllocations ? (
                    <span>Loading...</span>
                  ) : (
                    (allAllocationsSelected || noAllocationsSelected) && (
                      <span>All allocations selected</span>
                    )
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Shares to be sold" centerTitle>
                <StyledRow>
                  {numberToDisplayString(
                    Number(previousExitDetails?.sharesToBeSold || 0),
                    {
                      maximumFractionDigits: 10,
                    },
                  )}
                </StyledRow>
              </StyledFieldRow>
            </>
          )}

          <StyledFieldRow
            title="Consideration type"
            centerTitle
            tooltipMaxWidth={320}
            tooltipText={
              <span>
                If this exit transaction has both cash and share consideration:
                <ol
                  style={{
                    marginTop: '0.5rem',
                    marginBottom: 0,
                    paddingLeft: '1.5rem',
                  }}
                >
                  <li>
                    Select all selling share classes now, but process either the
                    cash or share consideration component first.
                  </li>
                  <li>
                    Once the first component is processed, update this exit (via
                    the Exits table), to process the other component.
                  </li>
                  <li>
                    If there is more than one class of share consideration being
                    issued in this exit (e.g. Ordinary and A Ordinary shares),
                    process each consideration share class as a separate update
                    (not as a new exit).
                  </li>
                </ol>
              </span>
            }
          >
            <StyledRow>
              <Controller
                name="considerationType"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppSimpleSelect
                    fullWidth
                    value={field.value}
                    onChange={(e) => {
                      const newConsiderationType = e.target
                        .value as ConsiderationType;
                      field.onChange(newConsiderationType);

                      // Clear errors for fields that are no longer relevant
                      if (newConsiderationType === ConsiderationType.Cash) {
                        clearErrors('shareConsiderationCompanyId');
                        clearErrors('shareConsiderationShareClassId');
                        clearErrors('totalConsiderationShares');
                        clearErrors('shareConsiderationValuePerShare');
                        clearErrors('shareConsiderationValueTotal');
                      } else if (
                        newConsiderationType === ConsiderationType.Share
                      ) {
                        clearErrors('sharePrice');
                        clearErrors('taxableValueSharePrice');
                        clearErrors('chargeAccruedFees');
                      }
                    }}
                    options={[
                      {
                        value: ConsiderationType.Cash,
                        label: 'Cash consideration',
                      },
                      {
                        value: ConsiderationType.Share,
                        label: 'Share consideration',
                      },
                    ]}
                    placeholder="Select consideration type"
                    disabled={disableEdit}
                    error={!!error}
                    errorMessage={error?.message}
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>
          <CashConsiderationFields
            control={control}
            setValue={setValue}
            errors={errors}
            isEditMode={isEditMode}
            disableEdit={disableEdit}
            considerationType={considerationType}
            totalConsiderationShares={totalConsiderationShares}
            taxReliefElegible={taxReliefElegible}
            isReceivedValueSharePriceEdited={isReceivedValueSharePriceEdited}
            firmId={firmId}
            selectedShareClasses={selectedShareClasses}
            selectedAllocations={selectedAllocations}
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            previousExitDetails={previousExitDetails}
          />
          <ShareConsiderationFields
            control={control}
            setValue={setValue}
            errors={errors}
            isEditMode={isEditMode}
            disableEdit={disableEdit}
            considerationType={considerationType}
            shareConsiderationCompanyId={shareConsiderationCompanyId}
            totalConsiderationShares={totalConsiderationShares}
            shareConsiderationAllocationMethod={
              shareConsiderationAllocationMethod
            }
            taxReliefElegible={taxReliefElegible}
            firmId={firmId}
            selectedShareClasses={selectedShareClasses}
            selectedAllocations={selectedAllocations}
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            filteredConsiderationCompanies={filteredConsiderationCompanies}
            previousExitDetails={previousExitDetails}
            lastManuallyEditedField={lastManuallyEditedField}
            calculateShareConsiderationValues={
              handleCalculateShareConsiderationValues
            }
          />
        </Spacing>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 8 }}>
        <Spacing>
          <GridContainer>
            <Grid size={{ md: 10, xs: 12 }} />
            <Grid size={{ md: 2, xs: 12 }}>
              {!disableEdit && (
                <ButtonGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFormSubmit(onSubmitForm)}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
          </GridContainer>
        </Spacing>
      </Grid>
    </GridContainer>
  );
};

export default ExitDetailsForm;
